import { etagFromRow, get, post, seg } from '../client';
import { ApiError, normalizePolicy } from '../errors';
import type { Page, ListParams } from '../types/common';
import type { RoleSummary } from '../types/auth';
import type {
  AccessReview, CreateAccessReviewRequest, CreateMembershipRequest, CreateOrgUnitRequest, CreateSupportCapabilityRequest,
  CreateTenantRequest, CreateUserRequest, Membership, OrgUnit, Permission, PolicyDecideRequest, PolicyDecideResponse, Role, RolePermissions,
  GovernanceRequest, SupportCapability, SupportScope, Tenant, TenantApprovalKind, TenantStatus, TenantTransition, TenantTransitionRequest, User,
} from '../types/admin';

/* ------------------------------------------------------------------------------------------------
 * Normalizers: core-api DTO → UI type. Each tolerates the legacy mock (already UI-shaped) payload.
 * ---------------------------------------------------------------------------------------------- */

type Raw = Record<string, unknown>;
const str = (v: unknown): string | null => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : null);
const bool = (v: unknown) => v === true;
const rv = (r: Raw) => (r.rowVersion ?? r.row_version ?? null) as string | number | null;
const tagOf = (r: Raw) => (typeof r.etag === 'string' ? r.etag : etagFromRow(rv(r)));
const humanize = (id: string) => {
  const s = id.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const day = (v: unknown): string => (typeof v === 'string' ? v.slice(0, 10) : '');

/** core-api tenant lifecycle table (organization/routes.ts) — command → from-states → to-state. */
export const TENANT_LIFECYCLE: { command: TenantTransition; from: TenantStatus[]; to: TenantStatus }[] = [
  { command: 'activate', from: ['pending_validation', 'restricted', 'suspended'], to: 'active' },
  { command: 'restrict', from: ['active'], to: 'restricted' },
  { command: 'suspend', from: ['active', 'restricted'], to: 'suspended' },
  { command: 'request_export', from: ['active', 'restricted', 'suspended'], to: 'export_pending' },
  { command: 'place_hold', from: ['active', 'restricted', 'suspended', 'export_pending', 'retention'], to: 'legal_hold' },
  { command: 'release_hold', from: ['legal_hold'], to: 'export_pending' },
  { command: 'terminate', from: ['export_pending'], to: 'terminated' },
  { command: 'retain', from: ['terminated'], to: 'retention' },
  { command: 'approve_disposition', from: ['retention'], to: 'approved_disposition' },
];
export const tenantTransitionsFrom = (s: TenantStatus): TenantTransition[] => TENANT_LIFECYCLE.filter((t) => t.from.includes(s)).map((t) => t.command);

export function normalizeTenant(raw: unknown): Tenant {
  const r = (raw ?? {}) as Raw;
  const status = (str(r.status) ?? 'pending_validation') as TenantStatus;
  const legacy = Array.isArray(r.allowedTransitions);
  return {
    id: String(r.id),
    slug: str(r.slug) ?? '',
    name: str(r.name) ?? '',
    status,
    region: str(r.region) ?? '',
    cell: str(r.cell),
    release: str(r.release),
    createdAt: str(r.createdAt) ?? '',
    updatedAt: str(r.updatedAt),
    userCount: typeof r.userCount === 'number' ? r.userCount : null,
    facilityCount: typeof r.facilityCount === 'number' ? r.facilityCount : null,
    allowedTransitions: legacy ? (r.allowedTransitions as TenantTransition[]) : tenantTransitionsFrom(status),
    history: Array.isArray(r.history) ? (r.history as Tenant['history']) : [],
    flags: legacy
      ? undefined
      : {
          validationPassed: bool(r.validationPassed),
          exportCompleted: bool(r.exportCompleted),
          holdActive: bool(r.holdActive),
          retentionSatisfied: bool(r.retentionSatisfied),
          ownershipReviewed: bool(r.ownershipReviewed),
        },
    rowVersion: rv(r),
    etag: tagOf(r),
  };
}

export function normalizeOrgUnit(raw: unknown, now = new Date()): OrgUnit {
  const r = (raw ?? {}) as Raw;
  const from = day(r.effectiveFrom);
  const to = r.effectiveTo ? day(r.effectiveTo) : null;
  const status =
    r.status === 'active' || r.status === 'inactive' || r.status === 'future'
      ? (r.status as OrgUnit['status'])
      : r.effectiveTo && new Date(String(r.effectiveTo)) <= now
        ? 'inactive'
        : r.effectiveFrom && new Date(String(r.effectiveFrom)) > now
          ? 'future'
          : 'active';
  return {
    id: String(r.id),
    parentId: (r.parentId as string | null) ?? null,
    kind: (str(r.kind) ?? 'location') as OrgUnit['kind'],
    name: str(r.name) ?? '',
    code: str(r.code) ?? '',
    timezone: str(r.timezone) ?? undefined,
    effectiveFrom: from,
    effectiveTo: to,
    status,
    supersedesId: (r.supersedesId as string | null) ?? null,
    rowVersion: rv(r),
    etag: tagOf(r),
  };
}

export function normalizeAdminUser(raw: unknown, roles: RoleSummary[] = []): User {
  const r = (raw ?? {}) as Raw;
  if (Array.isArray(r.roles) && typeof r.status === 'string') return r as unknown as User;
  const disabled = bool(r.disabled);
  const invited = r.enrollmentStatus === 'invited' || r.enrollmentComplete === false;
  return {
    id: String(r.id),
    username: str(r.username) ?? '',
    displayName: str(r.displayName) ?? str(r.username) ?? '',
    email: str(r.email),
    kind: (str(r.audience) ?? str(r.kind) ?? 'workforce') as User['kind'],
    status: invited ? 'invited' : disabled ? 'disabled' : 'active',
    mfa: null,
    lastLoginAt: null,
    roles,
    disabled,
    inviteToken: str(r.inviteToken) ?? undefined,
  };
}

export const roleSummary = (roleId: string, name?: string): RoleSummary => ({ id: roleId, key: roleId as RoleSummary['key'], name: name ?? humanize(roleId) });

export function normalizeMembership(raw: unknown, names: { users?: Map<string, string>; roles?: Map<string, string>; units?: Map<string, string> } = {}): Membership {
  const r = (raw ?? {}) as Raw;
  if (r.role && typeof r.role === 'object' && r.user) return { ...(r as unknown as Membership), etag: (r.etag as string) ?? etagFromRow(rv(r)) };
  const actorId = String(r.actorId ?? r.userId ?? '');
  const roleId = String(r.roleId ?? '');
  const locationId = (r.locationId as string | null) ?? null;
  const st = str(r.status);
  const from = str(r.effectiveFrom);
  return {
    id: String(r.id),
    userId: actorId,
    user: { id: actorId, display: names.users?.get(actorId) ?? actorId },
    role: roleSummary(roleId, names.roles?.get(roleId)),
    locationId,
    location: locationId ? { id: locationId, name: names.units?.get(locationId) ?? 'Location', facility: '', department: '' } : null,
    status: st === 'active' && from && new Date(from) > new Date() ? 'pending' : st === 'revoked' || st === 'inactive' ? 'revoked' : st === 'active' ? 'active' : st === 'pending_approval' || st === 'requested' || st === 'pending' ? 'pending' : 'revoked',
    effectiveFrom: day(r.effectiveFrom),
    effectiveTo: r.effectiveTo ? day(r.effectiveTo) : null,
    credentialExpiresAt: str(r.credentialExpiresAt),
    grantedBy: str(r.grantedBy),
    rowVersion: rv(r),
    etag: tagOf(r),
  };
}

export function normalizeRole(raw: unknown): Role {
  const r = (raw ?? {}) as Raw;
  const id = String(r.id);
  const name = str(r.name) ?? id;
  return {
    id,
    key: (str(r.key) ?? id) as Role['key'],
    name: name === id.replace(/_/g, ' ') || name === id ? humanize(id) : name,
    audience: str(r.audience) ?? undefined,
    description: str(r.description),
    userCount: typeof r.userCount === 'number' ? r.userCount : 0,
    builtIn: typeof r.builtIn === 'boolean' ? r.builtIn : true,
  };
}

export function normalizePermission(raw: unknown): Permission {
  const r = (raw ?? {}) as Raw;
  const id = String(r.id ?? r.key);
  const key = str(r.key) ?? id;
  const group = str(r.group) ?? key.split('.')[0]!.replace(/_/g, ' ');
  return { id, key, label: str(r.label) ?? key, group: group.charAt(0).toUpperCase() + group.slice(1), description: str(r.description) ?? undefined, risk: r.risk === 'high' ? 'high' : undefined };
}

const SCOPE_SUMMARY = (scopes: string[]): SupportCapability['scope'] =>
  scopes.includes('patient.read') ? 'data_read' : scopes.includes('organization.read') ? 'logs' : 'configuration';

export function normalizeSupport(raw: unknown): SupportCapability {
  const r = (raw ?? {}) as Raw;
  if (typeof r.scope === 'string' && typeof r.ticket === 'string') return { ...(r as unknown as SupportCapability), etag: (r.etag as string) ?? etagFromRow(rv(r)) };
  const scopes = (Array.isArray(r.scope) ? r.scope : []) as SupportScope[];
  const expiresAt = str(r.expiresAt);
  const rawStatus = str(r.status) ?? 'requested';
  const expired = rawStatus !== 'revoked' && !!expiresAt && new Date(expiresAt) <= new Date();
  const created = str(r.createdAt) ?? '';
  const reasonCode = str(r.reasonCode) ?? '';
  return {
    id: String(r.id),
    ticket: reasonCode || String(r.id).slice(0, 8),
    requestedBy: str(r.requestedBy) ?? '',
    actorId: str(r.actorId),
    reason: reasonCode,
    reasonCode,
    scope: SCOPE_SUMMARY(scopes),
    scopes,
    patientId: str(r.patientId),
    status: expired ? 'expired' : (rawStatus as SupportCapability['status']),
    approvedBy: str(r.approvedBy),
    createdAt: created,
    expiresAt,
    durationMinutes: expiresAt && created ? Math.max(1, Math.round((new Date(expiresAt).getTime() - new Date(created).getTime()) / 60000)) : null,
    endReason: null,
    rowVersion: rv(r),
    etag: tagOf(r),
  };
}

export function normalizeAccessReview(raw: unknown): AccessReview {
  const r = (raw ?? {}) as Raw;
  return {
    id: String(r.id),
    reviewerId: String(r.reviewerId ?? ''),
    membershipId: String(r.membershipId ?? ''),
    decision: (r.decision === 'revoke' ? 'revoke' : 'retain'),
    reasonCode: str(r.reasonCode) ?? '',
    createdAt: str(r.createdAt) ?? '',
  };
}

/** core-api {decision:'permit'|'deny',reasons[],...} → UI PolicyDecision (shared normalizer from errors.ts). */
export function normalizeDecision(raw: unknown, permission: string): PolicyDecideResponse {
  const d = normalizePolicy(raw);
  if (!d) return { decision: 'deny', action: permission, inputs: [], policyVersion: 'policy' };
  return { ...d, action: d.action === 'This action' ? permission : d.action };
}

export function normalizeGovernanceRequest(raw: unknown): GovernanceRequest {
  const r = (raw ?? {}) as Raw;
  return { ...(r as unknown as GovernanceRequest), id: String(r.id), payload: (r.payload as Record<string, unknown>) ?? {}, rowVersion: rv(r), etag: tagOf(r) };
}

const mapPage = <T>(p: Page<unknown>, f: (x: unknown) => T): Page<T> => ({ ...p, items: (p.items ?? []).map(f) });
const toIso = (d?: string) => (!d ? undefined : /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00.000Z` : d);

/* ------------------------------------------------------------------------------------------------
 * API helpers (bodies in the core-api shape; If-Match/Idempotency-Key per route registration).
 * ---------------------------------------------------------------------------------------------- */

export const tenantsApi = {
  list: async (p: ListParams & { status?: string } = {}) => mapPage(await get<Page<unknown>>('/tenants', { ...p }), normalizeTenant),
  /** Step-up; platform operator only. */
  create: async (body: CreateTenantRequest) => normalizeTenant(await post<unknown>('/tenants', body, { idempotent: true, stepUpLabel: `Create tenant ${body.name}` })),
  /** If-Match required (tenant rowVersion); step-up; reasonCode must match ^[A-Z][A-Z0-9_]{2,79}$. */
  transition: async (id: string, body: TenantTransitionRequest, etag: string | null | undefined) =>
    normalizeTenant(
      await post<unknown>(`/tenants/${seg(id)}:transition`, body, { ifMatch: etag, idempotent: true, stepUpLabel: `Change tenant state: ${body.command.replace(/_/g, ' ')}` }),
    ),
  /** Ask a second operator to approve terminate / release_hold / approve_disposition / ownership_review. If-Match = TENANT ETag. */
  requestApproval: async (id: string, body: { command: TenantApprovalKind; reasonCode: string }, tenantEtag: string | null | undefined) =>
    normalizeGovernanceRequest(await post<unknown>(`/tenants/${seg(id)}/approvals`, body, { ifMatch: tenantEtag, idempotent: true, stepUpLabel: `Request approval: ${body.command.replace(/_/g, ' ')}` })),
  /** Independent approval by another platform operator. If-Match = the REQUEST's ETag (W/"1" when fresh). */
  approveApproval: async (id: string, approvalId: string, reasonCode: string, etag: string | null | undefined = 'W/"1"') =>
    normalizeGovernanceRequest(await post<unknown>(`/tenants/${seg(id)}/approvals/${seg(approvalId)}:approve`, { reasonCode }, { ifMatch: etag, idempotent: true, stepUpLabel: 'Approve tenant lifecycle request' })),
};

export const orgApi = {
  /** core-api ignores asOf/includeInactive: status is computed against `asOf` and filtered client-side. */
  units: async (p: ListParams & { asOf?: string; includeInactive?: boolean } = {}) => {
    const { asOf, includeInactive, ...rest } = p;
    const at = asOf ? new Date(`${asOf}T23:59:59`) : new Date();
    const page = mapPage(await get<Page<unknown>>('/organization-units', { limit: 100, ...rest, asOf, includeInactive }), (u) => normalizeOrgUnit(u, at));
    return includeInactive === false || (asOf && !includeInactive) ? { ...page, items: page.items.filter((u) => u.status === 'active') } : page;
  },
  /** `etag` is required only when `supersedesId` is set (the replaced unit's ETag). */
  createUnit: async (body: CreateOrgUnitRequest, etag?: string | null) =>
    normalizeOrgUnit(await post<unknown>('/organization-units', { ...body, effectiveFrom: toIso(body.effectiveFrom) }, { ifMatch: body.supersedesId ? etag : undefined })),
};

export const usersApi = {
  /** core-api ignores q/status/limit today; filter client-side as well (see filterUsers). */
  list: async (p: ListParams & { q?: string; status?: User['status'] } = {}) => {
    const page = mapPage(await get<Page<unknown>>('/users', { ...p }), (u) => normalizeAdminUser(u));
    return { ...page, items: filterUsers(page.items, p.q, p.status) };
  },
  /** Invite (step-up + Idempotency-Key): enrollment goes to the email; the identity stays disabled until enrolled. */
  create: async (body: CreateUserRequest) => normalizeAdminUser(await post<unknown>('/users', body, { idempotent: true, stepUpLabel: `Invite ${body.username}` })),
  memberships: async (p: ListParams & { userId?: string; roleId?: string } = {}, names?: Parameters<typeof normalizeMembership>[1]) => {
    const page = mapPage(await get<Page<unknown>>('/memberships', { limit: 100, ...p }), (m) => normalizeMembership(m, names));
    // core-api lists every membership in the tenant; scope client-side.
    return { ...page, items: page.items.filter((m) => (!p.userId || m.userId === p.userId) && (!p.roleId || m.role.id === p.roleId)) };
  },
  /** Every membership in the tenant (all pages, bounded). */
  allMemberships: async (names?: Parameters<typeof normalizeMembership>[1]) => {
    const items: Membership[] = [];
    let cursor: string | null | undefined;
    for (let i = 0; i < 20; i++) {
      const page: Page<unknown> = await get<Page<unknown>>('/memberships', { limit: 100, cursor: cursor ?? undefined });
      items.push(...page.items.map((m) => normalizeMembership(m, names)));
      cursor = page.nextCursor;
      if (!cursor) break;
    }
    return items;
  },
  /**
   * Request a role grant (step-up + Idempotency-Key). core-api returns a governance request
   * `{id, status:'pending_approval', actorId, roleId}`; an independent privacy/security approver activates it.
   */
  createMembership: async (body: CreateMembershipRequest) =>
    normalizeMembership(await post<unknown>('/memberships', body, { idempotent: true, stepUpLabel: 'Request a role grant' })),
  /** Pending role-grant requests (membership.approve). */
  membershipRequests: async (p: ListParams = {}) => mapPage(await get<Page<unknown>>('/membership-requests', { limit: 100, ...p }), normalizeGovernanceRequest),
  /** Independent approval (privacy/security; not the requester or the grantee). If-Match + step-up + Idempotency-Key. */
  approveMembershipRequest: async (id: string, reasonCode: string, etag: string | null | undefined) =>
    normalizeMembership(await post<unknown>(`/membership-requests/${seg(id)}:approve`, { reasonCode }, { ifMatch: etag, idempotent: true, stepUpLabel: 'Approve role grant' })),
  /** If-Match required (membership rowVersion); step-up. core-api takes no body (no reason). */
  revokeMembership: (id: string, etag: string | null | undefined) =>
    post<{ id: string; status: 'revoked'; rowVersion: number }>(`/memberships/${seg(id)}:revoke`, {}, { ifMatch: etag, idempotent: true, stepUpLabel: 'Revoke a role' }),
};

export function filterUsers(items: User[], q?: string, status?: string): User[] {
  const needle = (q ?? '').trim().toLowerCase();
  return items.filter(
    (u) =>
      (!status || u.status === status) &&
      (!needle || u.displayName.toLowerCase().includes(needle) || u.username.toLowerCase().includes(needle) || (u.email ?? '').toLowerCase().includes(needle)),
  );
}

export const rolesApi = {
  list: async () => mapPage(await get<Page<unknown>>('/roles', { limit: 100 }), normalizeRole),
  permissions: async () => mapPage(await get<Page<unknown>>('/permissions', { limit: 100 }), normalizePermission),
  /** core-api: { roleId, permissions[] } for one role. */
  rolePermissions: async (roleId: string): Promise<RolePermissions> => {
    const r = await get<Raw>(`/roles/${seg(roleId)}/matrix`);
    if (Array.isArray(r.permissions)) return { roleId: String(r.roleId ?? roleId), permissions: r.permissions as string[] };
    // Legacy mock: the full UI matrix — take rows whose cell for this role grants anything.
    const granted: string[] = [];
    for (const g of (r.groups as { rows: { id: string; cells: Record<string, unknown> }[] }[] | undefined) ?? [])
      for (const row of g.rows) {
        const c = Object.entries(row.cells).find(([k]) => k === roleId || k.endsWith(roleId))?.[1];
        const st = typeof c === 'string' ? c : (c as { state?: string } | undefined)?.state;
        if (st && !['none', 'deny', 'prohibited'].includes(st)) granted.push(row.id);
      }
    return { roleId, permissions: granted };
  },
  /**
   * Decision for the caller's active context (core-api does not evaluate other roles). A deny comes back as 403 with
   * the decision in `problem.policy`; it is returned as a normal deny decision here.
   */
  decide: async (body: PolicyDecideRequest): Promise<PolicyDecideResponse> => {
    try {
      return normalizeDecision(await post<unknown>('/policies:decide', body), body.permission);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403 && e.policy) return normalizeDecision(e.policy, body.permission);
      throw e;
    }
  },
};

export const supportApi = {
  list: async (p: ListParams = {}) => mapPage(await get<Page<unknown>>('/support-capabilities', { limit: 100, ...p }), normalizeSupport),
  /** Step-up. */
  create: async (body: CreateSupportCapabilityRequest) =>
    normalizeSupport(await post<unknown>('/support-capabilities', body, { idempotent: true, stepUpLabel: 'Request support access' })),
  /** If-Match + Idempotency-Key + step-up; approver must differ from requester and the support actor. */
  approve: async (id: string, etag: string | null | undefined) =>
    normalizeSupport(await post<unknown>(`/support-capabilities/${seg(id)}:approve`, {}, { ifMatch: etag, idempotent: true, stepUpLabel: 'Approve support access' })),
  /** If-Match + Idempotency-Key + step-up. core-api records no reason on revoke. */
  revoke: async (id: string, etag: string | null | undefined) =>
    normalizeSupport(await post<unknown>(`/support-capabilities/${seg(id)}:revoke`, {}, { ifMatch: etag, idempotent: true, stepUpLabel: 'Revoke support access' })),
};

export const accessReviewsApi = {
  list: async (p: ListParams = {}) => mapPage(await get<Page<unknown>>('/access-reviews', { limit: 100, ...p }), normalizeAccessReview),
  /** One decision on one membership. If-Match = the MEMBERSHIP's ETag; step-up. */
  create: async (body: CreateAccessReviewRequest, membershipEtag: string | null | undefined) =>
    normalizeAccessReview(await post<unknown>('/access-reviews', body, { ifMatch: membershipEtag, idempotent: true, stepUpLabel: 'Record access review decision' })),
};

